import { oc } from "@orpc/contract";
import { z } from "zod";

const SearchTransactionSchema = z
  .object({
    id: z.string(),
    rowIndex: z.number().nullable(),
    sourceFile: z.string(),
    sourceDivision: z.string(),
  })
  .catchall(z.union([z.string(), z.number()]).nullable());

const PaginationSchema = z.object({
  total: z.number().int().nonnegative(),
  limit: z.number().int().positive(),
  offset: z.number().int().nonnegative(),
  hasMore: z.boolean(),
});

const FileMetadataSchema = z.object({
  fileName: z.string(),
  lastModified: z.string(),
  totalRows: z.number().int().nonnegative(),
});

export const SearchRexelFileInputSchema = z.object({
  fileType: z.enum(["e844sd", "e844d", "invmas", "itemast", "vendr1"]),
  source: z.enum(["azure", "database"]).optional().default("azure"),
  fileDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .refine(
      (val) => {
        const [y, m, d] = val.split("-").map(Number);
        const date = new Date(y, m - 1, d);
        return (
          date.getFullYear() === y &&
          date.getMonth() === m - 1 &&
          date.getDate() === d
        );
      },
      { message: "Invalid calendar date" },
    )
    .optional(),
  company: z.string().optional(),
  division: z.string().optional(),
  // All named filters — each file type uses a subset
  branch: z.string().optional(),
  tradingPartner: z.string().optional(),
  invoiceNumber: z.string().optional(),
  // Invoice date range (YYYYMMDD), inclusive. Single date = same from/to.
  // Only consumed by the e844sd file type (invoice_date is a YYYYMMDD integer).
  invoiceDateFrom: z
    .string()
    .regex(/^\d{8}$/)
    .optional(),
  invoiceDateTo: z
    .string()
    .regex(/^\d{8}$/)
    .optional(),
  location: z.string().optional(),
  supplierNumber: z.string().optional(),
  itemNumber: z.string().optional(),
  vend: z.string().optional(),
  filters: z.record(z.string(), z.string()).optional(),
  limit: z.number().int().positive().max(500).optional().default(100),
  offset: z.number().int().nonnegative().optional().default(0),
  sortBy: z.string().optional(),
  sortDirection: z.enum(["asc", "desc"]).optional().default("desc"),
});

export const SearchRexelFileResponseSchema = z.object({
  transactions: z.array(SearchTransactionSchema),
  pagination: PaginationSchema,
  fileMetadata: z.array(FileMetadataSchema),
});

export const rexelFileSearchContracts = {
  search: oc
    .input(SearchRexelFileInputSchema)
    .output(SearchRexelFileResponseSchema)
    .route({
      method: "GET",
    }),
} as const;

export type SearchRexelFileInput = z.infer<typeof SearchRexelFileInputSchema>;
export type SearchRexelFileResponse = z.infer<
  typeof SearchRexelFileResponseSchema
>;
export type RexelFileType = SearchRexelFileInput["fileType"];
